import { Injectable, signal, NgZone, isDevMode } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class PwaInstallService {
  private static readonly SESSION_SHOWN_KEY = 'pwa_install_shown';
  deferredPrompt: any;
  showInstallPrompt = signal<boolean>(false);
  isStandalone = false;

  // Debug flag to force show the install button
  debugShowInstall = isDevMode();

  private alreadyShownThisSession(): boolean {
    try {
      return sessionStorage.getItem(PwaInstallService.SESSION_SHOWN_KEY) === '1';
    } catch {
      return false;
    }
  }

  private markShownThisSession(): void {
    try {
      sessionStorage.setItem(PwaInstallService.SESSION_SHOWN_KEY, '1');
    } catch {
      // ignore
    }
  }

  private setPromptVisible(visible: boolean): void {
    if (visible) {
      if (this.alreadyShownThisSession()) return;
      this.markShownThisSession();
    }
    this.showInstallPrompt.set(visible);
  }

  // Method to check if PWA requirements are met
  private async checkPWARequirements(): Promise<boolean> {
    const requirements = {
      manifest: false,
      serviceWorker: false,
      https: false,
      isAndroid: false,
      browserSupport: false
    };

    // Check HTTPS (required for PWA)
    requirements.https = location.protocol === 'https:' || location.hostname === 'localhost' || location.hostname === '127.0.0.1';

    // Check if Android
    requirements.isAndroid = /android/i.test(navigator.userAgent);

    // Check browser support
    const userAgent = navigator.userAgent.toLowerCase();
    requirements.browserSupport = /chrome|samsung|firefox|edge/.test(userAgent);

    // Check manifest
    try {
      const manifestLinks = document.querySelectorAll('link[rel="manifest"]');
      if (manifestLinks.length > 0) {
        requirements.manifest = true;
        // Try to fetch and validate manifest
        const manifestHref = (manifestLinks[0] as HTMLLinkElement).href;
        try {
          await fetch(manifestHref);
        } catch (error) {
          requirements.manifest = false;
        }
      }
    } catch (error) {
      // Could not check manifest
    }

    // Check service worker
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        requirements.serviceWorker = !!registration;
      } catch (error) {
        // Could not check service worker
      }
    }

    const allRequirementsMet = Object.values(requirements).every(req => req);

    return allRequirementsMet;
  }

  constructor(private zone: NgZone) {
    this.checkIfStandalone();
    this.initializeInstallPrompt();

    // Check PWA requirements immediately and also after delay
    this.checkPWARequirements();
    setTimeout(() => this.checkPWARequirements(), 2000);

    // Force show install prompt in development for testing
    if (isDevMode()) {
      setTimeout(() => {
        if (!this.isStandalone) {
          this.zone.run(() => {
            this.setPromptVisible(true);
          });
        }
      }, 5000);
    }
  }

  // Check if the app is already installed
  private checkIfStandalone(): void {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                         (window.navigator as any).standalone ||
                         document.referrer.includes('android-app://');

    this.isStandalone = isStandalone;

    // If in debug mode and not standalone, we'll show the install prompt anyway
    if (this.debugShowInstall && !isStandalone) {
      setTimeout(() => {
        this.zone.run(() => {
          this.setPromptVisible(true);
        });
      }, 3000);
    }
  }

  initializeInstallPrompt() {
    // Check if service worker is supported and registered
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready
        .then(() => {
          // Service Worker is ready
        })
        .catch(() => {
          // Service Worker registration failed
        });
    }

    // Listen for the beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', (e) => {
      // Prevent Chrome 76+ from automatically showing the prompt
      e.preventDefault();

      // Stash the event so it can be triggered later
      this.deferredPrompt = e;

      // Update UI to show install button (in NgZone to ensure Angular detects the change)
      this.zone.run(() => {
        this.setPromptVisible(true);
      });
    });

    // Listen for the appinstalled event
    window.addEventListener('appinstalled', () => {
      // Hide the install prompt
      this.zone.run(() => {
        this.showInstallPrompt.set(false);
        this.isStandalone = true;
      });

      // Clear the deferredPrompt
      this.deferredPrompt = null;
    });

    // Additional debugging for Android
    if (/android/i.test(navigator.userAgent)) {
      // Check if already installed
      setTimeout(() => {
        window.matchMedia('(display-mode: standalone)').matches;
      }, 3000);
    }
  }

  promptInstall() {
    if (this.deferredPrompt) {
      // Show the native prompt
      this.deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      this.deferredPrompt.userChoice.then((choiceResult: any) => {
        if (choiceResult.outcome === 'accepted') {
          // User accepted the install prompt
        } else {
          // User dismissed the install prompt
        }
        // We no longer need the prompt. Clear it
        this.deferredPrompt = null;
        this.zone.run(() => {
          this.showInstallPrompt.set(false);
        });
      });
    } else if (this.debugShowInstall) {
      // Detect user agent for specific instructions
      const userAgent = navigator.userAgent.toLowerCase();

      // For iOS devices
      if (/ipad|iphone|ipod/.test(userAgent)) {
        alert('To install this app on iOS: tap the Share button and then "Add to Home Screen".');
      }
      // For Android Chrome/Samsung Internet
      else if (/android/.test(userAgent)) {
        if (/chrome/.test(userAgent)) {
          alert('To install this app on Android Chrome: tap the menu (⋮) and select "Add to Home screen" or "Install app".');
        } else if (/samsungbrowser/.test(userAgent)) {
          alert('To install this app on Samsung Internet: tap the menu and select "Add page to" → "Home screen".');
        } else {
          alert('To install this app on Android: open the browser menu and look for "Add to Home Screen" or "Install App".');
        }
      }
      // For other browsers where we don't have the prompt
      else {
        alert('To install this app: open the browser menu and select "Install App" or "Add to Home Screen".');
      }

      // Hide the prompt after showing instructions
      this.zone.run(() => {
        this.showInstallPrompt.set(false);
      });
    }
  }

  // Method to get PWA status for debugging
  async getPWAStatus(): Promise<any> {
    const status = {
      isStandalone: this.isStandalone,
      hasInstallPrompt: !!this.deferredPrompt,
      showInstallPrompt: this.showInstallPrompt(),
      userAgent: navigator.userAgent,
      displayMode: window.matchMedia('(display-mode: standalone)').matches ? 'standalone' : 'browser',
      requirements: await this.checkPWARequirements(),
      currentUrl: window.location.href,
      isDevMode: isDevMode(),
      manifestPresent: document.querySelectorAll('link[rel="manifest"]').length > 0,
      serviceWorkerSupported: 'serviceWorker' in navigator
    };

    return status;
  }

  // Force show install prompt for testing (dev only)
  forceShowInstallPrompt(): void {
    if (isDevMode()) {
      this.zone.run(() => {
        this.showInstallPrompt.set(true);
      });
    }
  }

  dismissInstallPrompt(): void {
    this.markShownThisSession();
    this.showInstallPrompt.set(false);
  }
}
